import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiHandler } from "../../api";
import { API_BASE_URL, endpoint } from "../../api/endpoint";
import { useDispatch, useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";

export const useOrderDetails = ({ orderId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { addToast } = useToasts();

  const { authData, storeId } = useSelector((state) => state.loginSlice);
  const { orderId: OrderId } = useParams();
  const isMounted = useRef(true);

  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleGoBack = () => {
    navigate(-1);
  };

  let paramId = OrderId || orderId;
  useEffect(() => {
    const fetchOrderDetails = async () => {
      setLoading(true);
      setError(null);  
      try {
        const result = await apiHandler({
          url:  `${endpoint.GET_ORDER_DETAILS}${paramId}`,
          method: "GET",
          authToken: authData,
          dispatch,
          storeId,
        });

        if (isMounted.current && result?.data?.data) {
          setOrderData(result.data.data);
        }
      } catch (err) {

        if (isMounted.current) setError("Failed to fetch order details.");
      } finally {
        if (isMounted.current) setLoading(false);
      }
    };

    if (paramId) fetchOrderDetails();

    return () => {
      isMounted.current = false;
    };
  }, [paramId]);

  const calculateDaysRemaining = () => {
    if (!orderData?.order_details?.return_expires_at) return 0;
    const today = new Date();
    const expiryDate = new Date(orderData.order_details.return_expires_at);
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const isReturnExpired = () => calculateDaysRemaining() === 0;

  const handleGetInvoice = () => {
    const invoiceUrl = orderData.order_details.invoice_url;

    if (invoiceUrl) {
      window.open(invoiceUrl, "_blank");
    }
  };

  const handleSettlementRequest = useCallback(
    async (e) => {
      e.preventDefault();

      if (
        !orderData?.order_details?._id ||
        !orderData?.buyer_details?.buyer_app_id ||
        !orderData?.buyer_details?.buyer_app_uri ||
        !orderData?.seller_details?.seller_app_id ||
        !orderData?.seller_details?.seller_app_uri
      ) {
        addToast("Cannot send settlement request. Missing details.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      try {
        setLoading(true);

        const url = `${endpoint.SEND_SETTLEMENT_REQUEST}`;

        const payload = {
          order_ids: [orderData?.order_details?._id],
          app_details: {
            buyer_app_id: orderData?.buyer_details?.buyer_app_id,
            buyer_app_uri: orderData?.buyer_details?.buyer_app_uri,
            seller_app_id: orderData?.seller_details?.seller_app_id,
            seller_app_uri: orderData?.seller_details?.seller_app_uri,
          },
        };

        const response = await apiHandler({
          url,
          method: "POST",
          data: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
          },
          authToken: authData,
          dispatch,
          storeId,
        });

        // Determine status
        const responseStatus = response?.data?.status || response?.status;

        // Safely extract message and failed orders based on response structure
        const topLevelData = response?.data || {};
        const nestedData = topLevelData?.response?.data || {};

        const toastMessage =
          nestedData?.message || "Settlement request result received.";

        const failedOrdersList =
          topLevelData?.failedOrders?.filter((item) => item && item.orderId) ||
          nestedData?.failedOrders?.filter((item) => item && item.orderId) ||
          [];

        // Prepare failed order list for UI
        let failedOrdersContent = null;
        if (failedOrdersList.length > 0) {
          failedOrdersContent = (
            <div style={{ marginTop: "10px" }}>
              <strong>Failed Order:</strong>
              <ul style={{ marginTop: "5px", color: "#e74c3c" }}>
                {failedOrdersList.map((order, index) => (
                  <li key={index}>
                    <strong>Order ID:</strong> {order.orderId} <br />
                    <strong>Error:</strong> {order.error}
                  </li>
                ))}
              </ul>
            </div>
          );
        }

        addToast(
          <>
            <div>{toastMessage}</div>
            {failedOrdersContent}
          </>,
          {
            appearance: responseStatus === 200 ? "success" : "error",
            autoDismiss: true,
          }
        );
      } catch (error) {

        addToast("An unexpected error occurred during settlement.", {
          appearance: "error",
          autoDismiss: true,
        });
      } finally {
        setLoading(false);
      }
    },
    [authData, dispatch, orderData]
  );

  const handleRaiseRecon = useCallback(
    async (e) => {
      e.preventDefault();

      if (
        !orderData?.order_details?._id ||
        !orderData?.buyer_details?.buyer_app_id ||
        !orderData?.buyer_details?.buyer_app_uri ||
        !orderData?.seller_details?.seller_app_id ||
        !orderData?.seller_details?.seller_app_uri
      ) {
       
        addToast("Cannot send settlement request. Missing details.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      try {
        setLoading(true);

        const url = `${endpoint.RAISE_RECON}`;

        const payload = {
          order_ids: [orderData?.order_details?._id],
          app_details: {
            buyer_app_id: orderData?.buyer_details?.buyer_app_id, // <- temp fix for typo
            buyer_app_uri: orderData?.buyer_details?.buyer_app_uri,
            seller_app_id: orderData?.seller_details?.seller_app_id,
            seller_app_uri: orderData?.seller_details?.seller_app_uri,
          },
        };

        const response = await apiHandler({
          url,
          method: "POST",
          data: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
          },
          authToken: authData,
          dispatch,
          storeId,
        });

        const responseStatus = response?.data?.status || response?.status;
        const failedOrdersList =
          response?.data?.response?.data?.failedOrders?.filter(
            (item) => item && typeof item === "object" && item.orderId
          ) || [];

        let toastMessage = response?.data?.message;

        let failedOrdersContent = null;
        if (failedOrdersList.length > 0) {
          failedOrdersContent = (
            <div style={{ marginTop: "10px" }}>
              <strong>Failed Order:</strong>
              <ul style={{ marginTop: "5px", color: "#e74c3c" }}>
                {failedOrdersList.map((order, index) => (
                  <li key={index}>
                    <strong>Order ID:</strong> {order.orderId} <br />
                    <strong>Error:</strong> {order.error}
                  </li>
                ))}
              </ul>
            </div>
          );
        }

        addToast(
          <>
            <div>{toastMessage}</div>
            {failedOrdersContent}
          </>,
          {
            appearance: responseStatus === 200 ? "success" : "error",
            autoDismiss: true,
          }
        );
      } catch (error) {


        addToast(
          error.response?.data?.message || "Unexpected error occurred.",
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      } finally {
        setLoading(false);
      }
    },
    [authData, dispatch, orderData]
  );

  return {
    orderData,
    loading,
    error,
    calculateDaysRemaining,
    isReturnExpired,
    handleGetInvoice,
    handleSettlementRequest,
    handleRaiseRecon,
    handleGoBack,
  };
};
